import type { MeDto, ViewAs } from '@nsb/core';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError, setViewAs } from './api.ts';

async function fetchMe(): Promise<MeDto | null> {
  try {
    const me = await api<MeDto>('/me');
    // Someone other than an admin signed in on this browser: drop a leftover developer-mode choice.
    if (!me.canViewAs) setViewAs(null);
    return me;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

/** Who is signed in, or null. 401 is an answer here, not an error. */
export function useMe() {
  return useQuery<MeDto | null, ApiError>({ queryKey: ['me'], queryFn: fetchMe, staleTime: 60_000 });
}

export function isStaff(me: MeDto | null | undefined): boolean {
  return Boolean(me?.org);
}

export function isAdmin(me: MeDto | null | undefined): boolean {
  return me?.org?.role === 'admin';
}

export type View = 'admin' | ViewAs;

/**
 * Developer mode: see the app as an admin, a client or a sub. The API applies
 * the chosen role; the page reloads so nothing fetched under the old role
 * lingers. Inside a project, the switch stays on it if the new role can see it.
 */
export async function switchView(view: View, pathname: string): Promise<void> {
  setViewAs(view === 'admin' ? null : view);
  const projectId = /^\/p\/([^/]+)/.exec(pathname)?.[1];
  let target = '/';
  if (projectId && view === 'admin') target = `/p/${projectId}`;
  if (projectId && view === 'client') {
    const me = await api<MeDto>('/me').catch(() => null);
    if (me?.projects.some((p) => p.id === projectId)) target = `/p/${projectId}`;
  }
  window.location.assign(target);
}
